import React, { useRef, useState, useEffect } from "react";
import emailjs from "emailjs-com";
import { FaInstagram, FaFacebookF, FaWhatsapp, FaTiktok } from "react-icons/fa";
import { Helmet } from "react-helmet";
import "../styles/Contact.css";

const useVisible = (threshold = 0.1) => {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
};

const CONTACT_INFO = [
  {
    label: "Email",
    value: "info@codixlb.com",
    href: "mailto:info@codixlb.com",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
  },
  {
    label: "Phone / WhatsApp",
    value: "+961 81 233 235",
    href: "tel:+96181233235",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.89 10a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.82 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 8.91a16 16 0 0 0 7 7l.96-.96a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
      </svg>
    ),
  },
  {
    label: "Book a Call",
    value: "book.codixlb.com",
    href: "https://book.codixlb.com",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
    ),
  },
];

const SOCIALS = [
  { Icon: FaInstagram, label: "Instagram", href: "https://www.instagram.com/codix.lb" },
  { Icon: FaFacebookF, label: "Facebook",  href: "https://www.facebook.com/share/17fNY6Dqdk" },
  { Icon: FaTiktok,    label: "TikTok",    href: "https://www.tiktok.com/@codix.lb" },
  { Icon: FaWhatsapp,  label: "WhatsApp",  href: "https://wa.me/96181233235" },
];

const SUBJECTS = [
  "Website Development",
  "Mobile App Development",
  "SaaS Development",
  "AI Integration & Automation",
  "Custom System / Dashboard",
  "SEO Optimization",
  "Website Maintenance",
  "Hosting & Support",
  "General Inquiry",
  "Other / Not Listed",
];

function Contact() {
  const form = useRef();
  const [status, setStatus]   = useState(""); // "" | "sending" | "success" | "error"
  const [loading, setLoading] = useState(false);

  const [headerRef, headerVis] = useVisible(0.1);
  const [formRef, formVis]     = useVisible(0.08);

  const sendEmail = (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus("");

    emailjs
      .sendForm(
        "service_qecf5ot",
        "template_b34dp58",
        form.current,
        "iIs8AQySNmtk8Ko03"
      )
      .then(() => {
        setLoading(false);
        setStatus("success");
        form.current.reset();
      })
      .catch((err) => {
        setLoading(false);
        setStatus("error");
        console.error(err);
      });
  };

  return (
    <>
      <Helmet>
        <title>Contact Codix Tech | Start Your Project Today</title>
        <meta
          name="description"
          content="Contact Codix Tech — Lebanon's full-service digital agency. Get a free consultation for web development, mobile apps, SaaS, AI integration, and more."
        />
        <meta name="keywords" content="Contact Codix Tech, Web Development Lebanon, Hire Web Developer" />
        <meta name="author" content="Codix Tech" />
      </Helmet>

      <section id="contact" className="contact-section">
        <div className="about-rule" aria-hidden="true" />
        <div className="contact-wrap">

          {/* ── Left: info ── */}
          <div
            ref={headerRef}
            className={`contact-info-col ${headerVis ? "contact-info-col--in" : ""}`}
          >
            <span className="cdx-label">// 05 — contact us</span>
            <h2 className="cdx-title">
              &lt;<span className="orange">Contact</span> Codix/&gt;
            </h2>
            <div className="cdx-rule" />
            <p className="contact-lead">
              Ready to start a project or just want to ask a question?
              Pick a channel below or fill the form — we reply fast.
            </p>

            {/* Contact items */}
            <div className="contact-items">
              {CONTACT_INFO.map(({ label, value, href, icon }, i) => (
                <a
                  key={label}
                  href={href}
                  className={`contact-item ${headerVis ? "contact-item--in" : ""}`}
                  style={{ transitionDelay: `${150 + i * 100}ms` }}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                >
                  <span className="contact-item-icon">{icon}</span>
                  <div>
                    <span className="contact-item-label">{label}</span>
                    <span className="contact-item-value">{value}</span>
                  </div>
                </a>
              ))}
            </div>

            {/* Socials */}
            <div className="contact-socials">
              <p className="contact-socials-label">Follow us</p>
              <div className="social-icons">
                {SOCIALS.map(({ Icon, label, href }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Codix Tech ${label}`}
                    className="social-icon"
                  >
                    <Icon />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* ── Right: form ── */}
          <div
            ref={formRef}
            className={`contact-form-col ${formVis ? "contact-form-col--in" : ""}`}
          >
            <form ref={form} onSubmit={sendEmail} className="contact-form" noValidate>

              
                <div className="form-field">
                  <label htmlFor="user_name">Full Name</label>
                  <input
                    id="user_name"
                    type="text"
                    name="user_name"
                    placeholder="Your Name"
                    required
                    autoComplete="name"
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="user_email">Email Address</label>
                  <input
                    id="user_email"
                    type="email"
                    name="user_email"
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                  />
                </div>
              

              <div className="form-field">
                <label htmlFor="subject">Subject</label>
                <select id="subject" name="subject" required>
                  <option value="">Select a subject…</option>
                  {SUBJECTS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label htmlFor="message">Message</label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Tell us about your project, goals, and timeline…"
                  required
                  rows={6}
                />
              </div>

              <button
                type="submit"
                className={`contact-btn ${loading ? "contact-btn--loading" : ""}`}
                disabled={loading}
                aria-label="Send Message"
              >
                {loading ? (
                  <>
                    <span className="btn-spinner" />
                    Sending…
                  </>
                ) : (
                  <>
                    Send Message
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M22 2L11 13"/><path d="M22 2L15 22 11 13 2 9l20-7z"/>
                    </svg>
                  </>
                )}
              </button>

              {status === "success" && (
                <div className="form-alert form-alert--success" role="alert">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  Message sent successfully! We'll get back to you shortly.
                </div>
              )}

              {status === "error" && (
                <div className="form-alert form-alert--error" role="alert">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                  Failed to send. Please try again or reach us directly.
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

export default Contact;